/**
 * Every figure the wiki quotes, keyed by its `@savi/core` export name.
 *
 * The numbers do not live in the sentences. A paragraph naming five numbers
 * yields five digits and nothing saying which is which, so a test over prose
 * degrades to `expect(text).toContain("14-period")` — which a reword breaks
 * and an invented number survives. Keyed by export name, the same test is an
 * exhaustive comparison. See `thresholds.test.ts`.
 *
 * Add an entry before you quote a number, not after. A figure typed straight
 * into a sentence is unchecked, and unchecked is the state this file exists
 * to prevent.
 */
export const QUOTED = {
  // strategies/ema-cross.ts
  EMA_CROSS_FAST: 9,
  EMA_CROSS_SLOW: 21,
  EMA_CROSS_MIN_BARS: 105,

  // strategies/hourly-cross.ts
  HOURLY_CROSS_EMA_PERIOD: 20,
  HOURLY_CROSS_MIN_BUCKET: 3,
  HOURLY_CROSS_MIN_BARS: 100,

  // strategies/volume-breakout.ts
  VOLUME_BREAKOUT_WINDOW: 50,
  VOLUME_BREAKOUT_MULTIPLE: 2,

  // strategies/momentum-pivot.ts
  MOMENTUM_RSI_PERIOD: 14,
  MOMENTUM_RSI_OVERSOLD: 35,
  MOMENTUM_RSI_OVERBOUGHT: 65,
  MOMENTUM_PIVOT_MIN_BARS: 70,

  // strategies/gap-detector.ts
  GAP_ATR_PERIOD: 14,
  GAP_MIN_ATR: 0.5,

  // strategies/prophet-swing.ts and prophet/*
  PROPHET_SWING_MIN_BARS: 30,
  PROPHET_SWING_SESSION_BUCKET: 1,
  STOCH_OVERSOLD: 20,
  STOCH_OVERBOUGHT: 80,
  STOCH_OVERBOUGHT_FOR_OSCILLATION: 80,
  OSCILLATION_WINDOW_SESSIONS: 60,
  CROSS_LOOKBACK_SESSIONS: 3,
  GREEN_GRACE_SESSIONS: 2,
  MIN_COMPLETED_WEEKS: 21,
  TREND_LOOKBACK_WEEKS: 8,
  LEG1_LOOKBACK_WEEKS: 2,
  OVERSOLD_LOOKBACK_SESSIONS: 5,
  FAST_SMA_PERIOD: 5,
  SLOW_SMA_PERIOD: 15,
  PIVOT_LOOKBACK_SESSIONS: 10,
  PIVOT_BUFFER_ATR: 0.1,
  DAILY_ATR_PERIOD: 14,

  // hourly/confluence.ts
  CONFLUENCE_FLOOR: 2,
  CONFLUENCE_STRONG_FLOOR: 3,

  // technical.ts and severity.ts
  TECHNICAL_52W_WINDOW: 252,
  TECHNICAL_VOLUME_WINDOW: 60,
  VOLUME_MULTIPLE_FLOOR: 2,
  TECHNICAL_URGENT_VOLUME_MULTIPLE: 5,

  // swing/ict/params.ts
  SWING_ATR_PERIOD: 14,
  SWING_FVG_MIN_ATR: 0.5,
  SWING_OB_MIN_MOVE_ATR: 0.5,
  SWING_OB_FORWARD_BARS: 4,
  SWING_ZONE_PROXIMITY_ATR: 0.25,
  SWING_STOP_BUFFER_ATR: 0.1,
  SWING_EQUAL_LEVEL_THRESHOLD: 0.001,
  SWING_DISPLACEMENT_THRESHOLD: 0.6,
  SWING_DAILY_LOOKBACK: 3,
  SWING_WEEKLY_LOOKBACK: 2,
  SWING_MIN_DAILY_BARS: 60,
  SWING_MIN_WEEKLY_BARS: 12,
  SWING_DAILY_LOOKBACK_SESSIONS: 400,

  // events/ex-date-avoid.ts
  EX_DATE_AVOID_SESSIONS: 21,
} as const;

/**
 * The same contract for constants that are `Set`s rather than numbers.
 *
 * Separate because `toBe` on a `Set` compares identity, so the test has to
 * spread and sort. One record of numbers and one of member lists keeps that
 * branch out of the loop.
 */
export const QUOTED_SETS = {
  RANK_ACTIONABLE_DECILES: [1, 10],
} as const;
